import { ReactNode, useCallback } from 'react';
import { ConfigurationStep } from '~/app/compositions/sidebar/configuration-step/configuration-step';
import { SeatCovers } from '~/app/compositions/sidebar/interior/components/seat-covers';
import { Seats } from '~/app/compositions/sidebar/interior/components/seats';
import { StyledInteriorContainer } from '~/app/compositions/sidebar/interior/styles/interior-styles';
import { OptionsHeader } from '~/app/compositions/sidebar/options-header';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useImages } from '~/graphql/hooks/use-images';
import { useSteps } from '~/graphql/hooks/use-steps';

export function Interior({ combinations }: { combinations?: ('seats' | 'covers')[] }) {
	const { isLoading } = useConfigurator();
	const { i18n } = useI18n();
	const { interiorTiles } = useCatalog();
	const interiorGalleryMedia = interiorTiles?.find((a) => a.status.selected)?.galleryMedia;
	const { mediaByProfileId } = useImages();
	const { config } = useMarketConfig();

	const getInterior = () => {
		if (!combinations)
			return (
				<>
					<Seats step={step} total={total} />
					<SeatCovers step={step} total={total} />
				</>
			);

		const nodes: ReactNode[] = [];

		for (const item of combinations) {
			if (item === 'seats') nodes.push(<Seats key="seats" step={step} total={total} />);
			if (item === 'covers') nodes.push(<SeatCovers key="seatCovers" step={step} total={total} />);
		}

		return nodes;
	};

	const getCombination = () => {
		if (!combinations) return 'interior';
		if (combinations && combinations.length > 0 && combinations.includes('seats')) return 'interior[seats]';
		if (combinations && combinations.length > 0 && combinations.includes('covers')) return 'interior[covers]';
		return 'interior';
	};

	const { step, total } = useSteps(getCombination());

	const impressionTrackingRef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading && total) {
				appTrackingService.selectionStepImpression({
					node,
					value: 'interior',
					elementCurrentStep: step,
					elementTotalSteps: total,
				});
			}
		},
		[isLoading],
	);

	const categoryDisclaimer = config.categoryDisclaimers?.find((d) => d.step === getCombination())?.disclaimer;

	return (
		<ConfigurationStep
			ref={impressionTrackingRef}
			id={'interior'}
			galleryType={'singleView'}
			media={[
				[mediaByProfileId[ImageViewsEnum.INTERIOR_SEATS_SEATS], interiorGalleryMedia!],
				[mediaByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD]],
				[mediaByProfileId[ImageViewsEnum.INTERIOR_REAR_REARSEATS]],
			]}
			headline={
				<OptionsHeader id={'interior'} title={i18n('ngc.equipment.interior')} step={step} total={total} />
			}
			disclaimer={categoryDisclaimer}>
			<StyledInteriorContainer>{getInterior()}</StyledInteriorContainer>
		</ConfigurationStep>
	);
}
